// ============================================================================
// PHOTO AUTO-ASSIGN SERVICE
// ----------------------------------------------------------------------------
// Pure, side-effect-free function that matches imported site photos to the
// final Stage 4 scope. Returns the AI's per-photo dispositions + assignments + a
// hero-photo suggestion; the CALLER is responsible for applying the result to
// React state (setScope / setSelectedHeroImage / etc.). This keeps the logic
// reusable from App.jsx's attended final-scope watcher and explicit Stage 4
// retry. It must never run against Stage 2's provisional scope: the caller
// proves the final WMS/scope/cost projection before spending a vision call.
// ============================================================================

import { callOpenRouter, getMaxOutputTokens } from './ai-api';
import { devLog } from '../utils/dev-log';
import { getClientFacingPhoto } from './photo-client-images';
import { resolveClientScopeDescription } from '../utils/scope-descriptions';
import { validatePhotoAutoAssignOutput } from '../contracts/ai-output/photo-markup';
import { pickModelForTask } from './model-selector';
import {
    chooseQuoteHeroPhotoIndex,
    scoreQuoteHeroCandidate,
} from './quote-hero-image';
import { scopePhotoDispositionKey } from '../utils/scope-photo-authority';
import { isDocumentDerivedVisual } from '../utils/project-photo-contract';
import { fileReferenceUnavailableReason } from './file-reference-health';
import { isRawMinioUrl } from '../utils/minio-url';

const PHOTO_AUTO_ASSIGN_TASK = 'photo-auto-assign';
const PHOTO_AUTO_ASSIGN_FALLBACK_TASK = 'photo-auto-assign-fallback';
// 0 = no browser timer; the server deadline is the only bound (the standing
// "no timeouts on long AI pipelines" rule). Was 120_000, which on a photo-heavy
// job aborted mid-assignment and read as a cancel the operator never asked for.
const PHOTO_AUTO_ASSIGN_TIMEOUT_MS = 0;
const PHOTO_AUTO_ASSIGN_RESPONSE_FORMAT = Object.freeze({
    type: 'json_schema',
    json_schema: {
        name: 'photo_auto_assign',
        strict: true,
        schema: {
            type: 'object',
            additionalProperties: false,
            required: ['dispositions', 'heroPhotoIndex', 'reasoning'],
            // NO `minimum` / `maximum` ON INTEGER PROPERTIES. Read this before
            // "tightening" the schema.
            //
            // OpenRouter routes an Anthropic model to whichever upstream has
            // capacity, and both Amazon Bedrock and Azure REJECT numeric bounds
            // in a structured-output schema:
            //   400 "output_config.format.schema: For 'integer' type, property
            //        'minimum' is not supported"
            // Bedrock additionally returned "output_config.format: Extra inputs
            // are not permitted".
            //
            // Because the bound sits in the schema, the request is malformed for
            // that provider no matter which model is asked for — so the primary
            // (claude-haiku-4.5) AND the fallback (claude-sonnet-5) both 400 and
            // photo auto-assign fails completely. Confirmed against the live
            // ai_usage_events table on 2026-07-29: 5 failures, 0 successes, both
            // routes, provider_name Amazon Bedrock then Azure.
            //
            // Nothing is lost by dropping the bounds: every index is range-checked
            // in code after parsing, against the REAL batch/scope counts rather
            // than just >= 0 — see parseAndValidateAssignmentResponse. The
            // schema bound was belt-and-braces; the belt is in the code.
            properties: {
                dispositions: {
                    type: 'array',
                    items: {
                        type: 'object',
                        additionalProperties: false,
                        required: ['photoIndex', 'outcome', 'scopeIndex', 'reason'],
                        properties: {
                            photoIndex: { type: 'integer' },
                            outcome: { type: 'string', enum: ['assigned', 'omitted'] },
                            scopeIndex: { anyOf: [{ type: 'integer' }, { type: 'null' }] },
                            reason: {
                                type: 'string',
                                enum: [
                                    'assigned',
                                    'weak-quality',
                                    'blurred',
                                    'redundant',
                                    'off-topic',
                                    'no-matching-scope',
                                    'unavailable-image',
                                ],
                            },
                        },
                    },
                },
                heroPhotoIndex: { anyOf: [{ type: 'integer' }, { type: 'null' }] },
                reasoning: { type: 'string' },
            },
        },
    },
});

function isAbortLike(err) {
    return err?.name === 'AbortError'
        || /(?:abort|cancelled|canceled)/i.test(String(err?.message || ''));
}

function photoAutoAssignInvalidOutput(message, issues = []) {
    const err = new Error(message);
    err.name = 'PhotoAutoAssignOutputError';
    err.code = 'photo_auto_assign_invalid_output';
    err.retryable = true;
    err.issues = issues;
    return err;
}

function unwrapAssignmentEnvelope(parsed) {
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return parsed;
    for (const key of ['result', 'data', 'output']) {
        const candidate = parsed[key];
        if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
            if ('dispositions' in candidate || 'heroPhotoIndex' in candidate || 'hero_photo_index' in candidate) {
                return candidate;
            }
        }
    }
    return parsed;
}

function normaliseAssignmentEnvelope(parsed) {
    const envelope = unwrapAssignmentEnvelope(parsed);
    if (!envelope || typeof envelope !== 'object' || Array.isArray(envelope)) return envelope;
    const dispositions = Array.isArray(envelope.dispositions)
        ? envelope.dispositions.map(disposition => {
            if (!disposition || typeof disposition !== 'object' || Array.isArray(disposition)) return disposition;
            return {
                ...disposition,
                photoIndex: disposition.photoIndex ?? disposition.photo_index,
                scopeIndex: Object.prototype.hasOwnProperty.call(disposition, 'scopeIndex')
                    ? disposition.scopeIndex
                    : disposition.scope_index,
            };
        })
        : envelope.dispositions;
    return {
        ...envelope,
        dispositions,
        heroPhotoIndex: Object.prototype.hasOwnProperty.call(envelope, 'heroPhotoIndex')
            ? envelope.heroPhotoIndex
            : envelope.hero_photo_index,
    };
}

const AI_OMISSION_REASONS = new Set([
    'weak-quality',
    'blurred',
    'redundant',
    'off-topic',
    'no-matching-scope',
    'unavailable-image',
]);

async function parseAndValidateAssignmentResponse(content, { batchPhotoCount, scopeCount }) {
    if (!content || typeof content !== 'string') {
        throw photoAutoAssignInvalidOutput('Photo assignment AI returned an empty response.');
    }
    const { parseJsonSafe } = await import('../utils/json-parse-safe');
    const parsed = normaliseAssignmentEnvelope(parseJsonSafe(content, { context: PHOTO_AUTO_ASSIGN_TASK }));
    const validation = validatePhotoAutoAssignOutput(parsed);
    if (!validation.ok) {
        throw photoAutoAssignInvalidOutput(
            `Photo assignment AI returned invalid JSON: ${validation.issues.join('; ')}`,
            validation.issues,
        );
    }

    const value = validation.value;
    const issues = [];
    if (!Array.isArray(value.dispositions)) issues.push('dispositions must be an array');
    if (!(value.heroPhotoIndex === null || Number.isInteger(value.heroPhotoIndex))) {
        issues.push('heroPhotoIndex must be an integer or null');
    }
    if (typeof value.reasoning !== 'string') issues.push('reasoning must be a string');
    if (Array.isArray(value.dispositions)) {
        if (value.dispositions.length !== batchPhotoCount) {
            issues.push(`dispositions must contain exactly ${batchPhotoCount} entries`);
        }
        const seenPhotoIndices = new Set();
        value.dispositions.forEach((disposition, index) => {
            if (!disposition || typeof disposition !== 'object' || Array.isArray(disposition)) {
                issues.push(`dispositions[${index}] must be an object`);
                return;
            }
            if (!Number.isInteger(disposition.photoIndex)
                || disposition.photoIndex < 0
                || disposition.photoIndex >= batchPhotoCount) {
                issues.push(`dispositions[${index}].photoIndex is out of range`);
            } else if (seenPhotoIndices.has(disposition.photoIndex)) {
                issues.push(`dispositions[${index}].photoIndex is duplicated`);
            } else {
                seenPhotoIndices.add(disposition.photoIndex);
            }
            if (disposition.outcome === 'assigned') {
                if (!Number.isInteger(disposition.scopeIndex)
                    || disposition.scopeIndex < 0
                    || disposition.scopeIndex >= scopeCount) {
                    issues.push(`dispositions[${index}].scopeIndex is out of range`);
                }
                if (disposition.reason !== 'assigned') {
                    issues.push(`dispositions[${index}].reason must be assigned`);
                }
            } else if (disposition.outcome === 'omitted') {
                if (disposition.scopeIndex !== null) {
                    issues.push(`dispositions[${index}].scopeIndex must be null when omitted`);
                }
                if (!AI_OMISSION_REASONS.has(disposition.reason)) {
                    issues.push(`dispositions[${index}].reason is not an allowed omission reason`);
                }
            } else {
                issues.push(`dispositions[${index}].outcome is invalid`);
            }
        });
    }
    if (Number.isInteger(value.heroPhotoIndex)
        && (value.heroPhotoIndex < 0 || value.heroPhotoIndex >= batchPhotoCount)) {
        issues.push('heroPhotoIndex is out of range');
    }
    if (issues.length > 0) {
        throw photoAutoAssignInvalidOutput(
            `Photo assignment AI output failed validation: ${issues.join('; ')}`,
            issues,
        );
    }
    return value;
}

const DOCUMENT_OR_LOW_VALUE_PHOTO_PATTERNS = /\b(document|letter|email|form|invoice|receipt|report|certificate|quote|rfq|rfp|tender|correspondence|notice|agenda|minutes|schedule|plan|drawing|specification|approval|permit|table|text|page|scan|print|fax|memo|contract|agreement|screenshot)\b/i;

// v5.19.24: building-element keywords used to BOOST photos that are clearly
// site shots. Mirrors the bonus list in diagnostic-orchestrator.selectRelevantPhotos
// so the two filters score the same photo the same way.
const SITE_PHOTO_POSITIVE_PATTERNS = /\b(roof|wall|balcony|concrete|crack|spall|leak|membrane|gutter|drain|paint|rust|mould|mold|timber|brick|tile|floor|ceiling|window|door|stair|fence|carpark|driveway|entrance|hallway|bathroom|kitchen|laundry)\b/i;
const CAMERA_FILENAME_PATTERN = /^(IMG|DSC|DCIM|P\d|Photo|Site|Inspection)/i;

/**
 * Score a photo for "is this a real site photo or a paperwork scan?"
 * Higher = more likely a site photo. Returns a number; the threshold for
 * inclusion in auto-assign is +10 (matches selectRelevantPhotos in the
 * diagnostic orchestrator so both passes treat the same photo the same way).
 *
 * v5.19.24 (Peter 2026-05-05): pre-v5.19.24 the auto-assign service sent
 * EVERY photo — including OCR scans of the printed RFQ — to the assignment
 * AI. The AI then sometimes assigned RFQ scans to scope items, polluting
 * the client-facing quote. This score lets us drop documents before the
 * AI ever sees them. The same patterns power the hero photo scorer below.
 */
export function scoreSitePhotoLikelihood(photo) {
    if (!photo || typeof photo !== 'object') return -100;
    let score = 50;
    const label = (photo.aiLabel || photo.label || '').toLowerCase();
    const name = (photo.originalName || photo.name || '').toLowerCase();
    const description = (photo.description || photo.context || '').toLowerCase();

    // Penalty: document-like label or filename
    if (DOCUMENT_OR_LOW_VALUE_PHOTO_PATTERNS.test(label)) score -= 40;
    if (DOCUMENT_OR_LOW_VALUE_PHOTO_PATTERNS.test(name)) score -= 30;
    if (DOCUMENT_OR_LOW_VALUE_PHOTO_PATTERNS.test(description)) score -= 25;

    // Penalty: very small file (likely a screenshot/crop of text)
    if (photo.sizeKB && photo.sizeKB < 20) score -= 20;

    // Bonus: label mentions building elements
    if (SITE_PHOTO_POSITIVE_PATTERNS.test(label) || SITE_PHOTO_POSITIVE_PATTERNS.test(description)) {
        score += 20;
    }

    // Bonus: larger files are more likely real site photos (taken by camera)
    if (photo.sizeKB && photo.sizeKB > 100) score += 10;

    // Bonus: original filename looks like a camera photo
    if (CAMERA_FILENAME_PATTERN.test(name)) score += 10;

    // Penalty: already marked up — fine for hero/scope context but the
    // raw clean photo from the same source is preferred for scope items.
    if (photo.aiAnnotated) score -= 10;
    return score;
}

/**
 * Filter a photo array down to those likely to be real site photos. Returns
 * the surviving photos AND an `originalIndexFor` lookup so the caller can
 * translate AI-returned indices (which are into the FILTERED array) back to
 * the original photos array used by setScope.
 *
 * Threshold of +10 matches diagnostic-orchestrator.selectRelevantPhotos so
 * the two passes treat the same photo the same way.
 */
export function selectAssignablePhotos(photos) {
    if (!Array.isArray(photos) || photos.length === 0) {
        return { photos: [], originalIndexFor: () => -1, droppedCount: 0 };
    }
    // Exact producer provenance is authoritative and is applied BEFORE the
    // filename/label heuristics below. Keep original indexes so the AI's
    // filtered-array positions still map back to the caller's raw audit array.
    const candidates = photos
        .map((photo, idx) => ({ photo, idx }))
        .filter(({ photo }) => !isDocumentDerivedVisual(photo));
    const scored = candidates.map(({ photo, idx }) => ({
        photo,
        idx,
        score: scoreSitePhotoLikelihood(photo),
    }));
    const survivors = scored.filter(s => s.score > 10);
    const dropped = photos.length - survivors.length;
    // Preserve original photo order so the caller's index expectations stay sane
    survivors.sort((a, b) => a.idx - b.idx);
    const originalIndices = survivors.map(s => s.idx);
    const filteredPhotos = survivors.map(s => s.photo);
    return {
        photos: filteredPhotos,
        originalIndexFor: (filteredIdx) => {
            if (!Number.isInteger(filteredIdx) || filteredIdx < 0 || filteredIdx >= originalIndices.length) return -1;
            return originalIndices[filteredIdx];
        },
        droppedCount: dropped,
    };
}

function toIntegerIndex(value) {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isInteger(n) ? n : null;
}

/**
 * Build a map from photo array index → location string ("Unit 7 east balcony")
 * by joining photos to forensic observations via Stage 2's source_id format
 * ("photo-N", 1-indexed). Used by autoAssignPhotosToScope to inject locator
 * hints into the prompt so Unit 7 photos prefer Unit 7 scope items.
 *
 * v5.19.24 (Peter 2026-05-05): "Unit 7's photos should be allocated next to
 * Unit 7's scope item." Pre-v5.19.24 the prompt only saw `photo.aiLabel`,
 * which usually didn't mention the unit. The forensic pass already produces
 * `location_tag` per photo (e.g. "Unit 3A bathroom floor waste" — see
 * PHOTO_FORENSIC_PROMPT in src/config/prompts.js). This wires that signal
 * through to the assignment AI.
 *
 * @param {Array} photos                     The photos array used as the
 *                                           source of truth for indices.
 * @param {Array} forensicObservations       The observations[] from Stage 2's
 *                                           photoForensicResult / analysis.
 * @returns {Map<number, string>}            Sparse map: only photos with a
 *                                           non-empty locator are present.
 */
export function buildPhotoLocatorMap(photos, forensicObservations) {
    const out = new Map();
    if (!Array.isArray(photos) || photos.length === 0) return out;
    if (!Array.isArray(forensicObservations) || forensicObservations.length === 0) return out;

    // Bucket observations by source_id so we can look them up in O(1).
    // A photo can have multiple observations (different defects on the
    // same photo); we keep the first non-empty location_tag.
    const obsBySourceId = new Map();
    for (const obs of forensicObservations) {
        if (!obs || !obs.source_id) continue;
        const tag = String(obs.location_tag || '').trim();
        if (!tag) continue;
        if (!obsBySourceId.has(obs.source_id)) {
            obsBySourceId.set(obs.source_id, tag);
        }
    }

    // Forensic source_ids are 1-indexed: photo-1 → photos[0].
    for (let i = 0; i < photos.length; i++) {
        const sourceId = `photo-${i + 1}`;
        const tag = obsBySourceId.get(sourceId);
        if (tag) out.set(i, tag);
    }
    return out;
}

export function isLikelyDocumentPhoto(photo) {
    return scoreSitePhotoLikelihood(photo) <= 10;
}

// ============================================================================
// UNIT TOKEN EXTRACTION (v5.34.6)
// ----------------------------------------------------------------------------
// Peter 2026-05-19: "For Quotes that span multiple Units, I want QuoteMaker to
// figure out which unit the photos are for, to ensure they are assigned to
// the right unit. When Quotes have multiple Units, we respect that Strata
// Management requires quotes to be split out by Unit numbers, so when we do
// that, It would be great to have the correct matching photo of the right
// unit to the scope item with that unit."
//
// Strategy: extract normalised unit identifiers from any text source — scope
// item title/description, photo locator, photo aiLabel — and use the
// intersection to drive a HARD precedence in applyAutoAssignResult.
//
// Recognised forms (case-insensitive):
//   - "Unit 7", "Unit 7A", "Unit  7" (extra whitespace), "Unit-7"
//   - "Apartment 14", "Apt 14", "Apt. 14"
//   - "Lot 22", "Lot-22"
//   - "U/7", "U7" (only when clearly a unit identifier, not part of a word)
//   - "Level 3" — IGNORED (too generic; would over-match)
//   - "Strata Plan 12345" — IGNORED (different field)
//
// The output is a SET of canonical string tokens like "unit:7", "unit:7A",
// "apt:14", "lot:22". Unit/Apartment/Apt all canonicalise to "unit:" because
// strata buildings use the terms interchangeably; lots are separate (the
// strata lot number is a distinct identifier in the strata plan).
// ============================================================================

const UNIT_PATTERNS = [
    // Singular + plural ("Unit 7", "Units 7,8")
    /\bunits?[\s\-:_]*([0-9]+[A-Za-z]?)\b/gi,
    /\bapartments?[\s\-:_]*([0-9]+[A-Za-z]?)\b/gi,
    /\bapt\.?[\s\-:_]*([0-9]+[A-Za-z]?)\b/gi,
    /\bu\/([0-9]+[A-Za-z]?)\b/gi,
];

const LOT_PATTERNS = [
    /\blots?[\s\-:_]*([0-9]+[A-Za-z]?)\b/gi,
    /\bl\/([0-9]+[A-Za-z]?)\b/gi,
];

// Stop-words excluded from label-overlap scoring (Pass A disambiguation).
const OVERLAP_STOPWORDS = new Set([
    'the', 'a', 'an', 'and', 'or', 'of', 'to', 'in', 'on', 'at', 'for', 'with',
    'unit', 'units', 'apartment', 'apartments', 'apt', 'lot', 'lots',
    'east', 'west', 'north', 'south', 'left', 'right', 'side',
    'photo', 'image', 'pic', 'picture', 'shot', 'view',
    'jpg', 'jpeg', 'png', 'heic',
]);

function tokenisePhotoLabel(text) {
    if (!text || typeof text !== 'string') return new Set();
    const out = new Set();
    const words = text.toLowerCase().match(/[a-z][a-z0-9]{2,}/g) || [];
    for (const w of words) {
        if (OVERLAP_STOPWORDS.has(w)) continue;
        out.add(w);
    }
    return out;
}

function tokenOverlapScore(aTokens, bTokens) {
    if (!(aTokens instanceof Set) || !(bTokens instanceof Set)) return 0;
    if (aTokens.size === 0 || bTokens.size === 0) return 0;
    let n = 0;
    for (const t of aTokens) if (bTokens.has(t)) n++;
    return n;
}

export function extractUnitTokens(text) {
    if (!text || typeof text !== 'string') return new Set();
    const tokens = new Set();
    for (const pattern of UNIT_PATTERNS) {
        const matches = text.matchAll(pattern);
        for (const m of matches) {
            const id = String(m[1] || '').toUpperCase();
            if (id) tokens.add(`unit:${id}`);
        }
    }
    for (const pattern of LOT_PATTERNS) {
        const matches = text.matchAll(pattern);
        for (const m of matches) {
            const id = String(m[1] || '').toUpperCase();
            if (id) tokens.add(`lot:${id}`);
        }
    }
    return tokens;
}

/**
 * Returns true if two unit-token sets intersect (share at least one common
 * token). Used to detect "this photo's unit matches this scope item's unit".
 */
export function unitTokensIntersect(a, b) {
    if (!(a instanceof Set) || !(b instanceof Set)) return false;
    if (a.size === 0 || b.size === 0) return false;
    for (const tok of a) {
        if (b.has(tok)) return true;
    }
    return false;
}

/**
 * Ask the AI to match each photo in `photos` to the most relevant item in
 * `scope`, and to pick a hero photo for the quote cover.
 *
 * v5.19.24 (Peter 2026-05-05) added:
 *   - Document/RFQ-scan filtering via selectAssignablePhotos before sending
 *     to the AI (was a confirmed bug: paperwork scans were being assigned
 *     to scope items).
 *   - Locator-aware prompt: forensicObservations are joined to photos by
 *     source_id, and the locator string ("Unit 7 east balcony") is shown
 *     to the AI alongside each photo. A new rule tells the AI to prefer
 *     scope items whose title/description mention the same unit/area.
 *
 * Returned assignments use ORIGINAL photo indices (translated via the
 * filter's index map). Callers do NOT need to know about filtering.
 *
 * @param {Object} args
 * @param {Array<Object>} args.photos           Stage 1 photos array
 * @param {Array<Object>} args.scope            Stage 3 scope array
 * @param {string} args.apiKey                  OpenRouter key
 * @param {string} args.model                   OpenRouter model id
 * @param {AbortSignal} [args.signal]           Cancellation signal
 * @param {string} [args.projectId]             Pipeline attribution and Blob ownership boundary
 * @param {number} [args.maxImages]             How many photos to send (default 10)
 * @param {Array}  [args.forensicObservations]  Optional Stage 2 photoForensic
 *                                              observations[]; if supplied,
 *                                              location_tag values are joined
 *                                              to photos by source_id and
 *                                              shown to the AI for locator-
 *                                              aware allocation.
 * @returns {Promise<{assignments:Array, dispositions:Array, heroPhotoIndex:number|null, reasoning:string, droppedDocPhotos:number}|null>}
 *   Returns null when the call was aborted OR the response could not be parsed.
 *   Throws only on unrecoverable errors (missing apiKey, etc).
 */
export async function autoAssignPhotosToScope({
    photos,
    scope,
    apiKey,
    model,
    signal,
    projectId,
    maxImages = 10,
    forensicObservations = null,
}) {
    if (!apiKey) throw new Error('Missing apiKey');
    if (!Array.isArray(photos) || photos.length === 0) return null;
    if (!Array.isArray(scope) || scope.length === 0) return null;

    // v5.19.24: drop document/paperwork scans before the AI ever sees them.
    // Returned assignments are translated back to ORIGINAL photo indices via
    // originalIndexFor, so callers don't need to know about the filter.
    const filtered = selectAssignablePhotos(photos);
    if (filtered.photos.length === 0) {
        devLog(`[photo-auto-assign] All ${photos.length} photo(s) classified as documents — skipping assignment`);
        return {
            assignments: [],
            dispositions: [],
            heroPhotoIndex: null,
            reasoning: 'All photos classified as documents/paperwork',
            droppedDocPhotos: filtered.droppedCount,
            assignmentCoverage: {
                status: 'validated',
                assignablePhotos: 0,
                dispositionedPhotos: 0,
                assignedPhotos: 0,
                omittedPhotos: 0,
                complete: true,
                requiresReview: false,
            },
        };
    }
    if (filtered.droppedCount > 0) {
        devLog(`[photo-auto-assign] Dropped ${filtered.droppedCount} document/paperwork photo(s) before assignment`);
    }

    // v5.19.24: join photos to forensic observations by source_id so the
    // prompt can show "[3] crack on east balcony — at: Unit 7 east balcony"
    // and the assignment AI can prefer matching scope items.
    // The locator map is keyed by ORIGINAL photo index; translate to
    // FILTERED index for the prompt.
    const locatorByOrigIdx = buildPhotoLocatorMap(photos, forensicObservations);
    const locatorByFilteredIdx = new Map();
    for (let fIdx = 0; fIdx < filtered.photos.length; fIdx++) {
        const origIdx = filtered.originalIndexFor(fIdx);
        const tag = locatorByOrigIdx.get(origIdx);
        if (tag) locatorByFilteredIdx.set(fIdx, tag);
    }

    // Build compact summaries so the prompt stays small.
    const scopeSummary = scope.map((item, idx) => ({
        index: idx,
        code: item.code,
        title: item.title,
        description: resolveClientScopeDescription(item, { fallbackToTitle: false }) || item.description || '',
    }));
    const photoDescriptions = filtered.photos.map((photo, idx) => {
        const locator = locatorByFilteredIdx.get(idx) || '';
        // CN5: the model's label or the position, never the file name.
        const baseName = photo.aiLabel || `Photo ${idx + 1}`;
        return {
            index: idx,
            id: photo.id,
            name: locator ? `${baseName} — at: ${locator}` : baseName,
        };
    });
    const hasAnyLocator = locatorByFilteredIdx.size > 0;
    const batchSize = Math.max(1, Math.floor(Number(maxImages) || 10));

    // Resolve every assignable photo. Missing bytes are represented by a
    // deterministic omitted disposition instead of silently disappearing.
    const resolvable = [];
    const dispositions = [];
    for (let filteredIndex = 0; filteredIndex < filtered.photos.length; filteredIndex++) {
        if (signal?.aborted) return null;
        const photo = getClientFacingPhoto(filtered.photos[filteredIndex]);
        const dataUrl = await resolvePhotoDataUrl(photo, projectId);
        if (dataUrl) {
            resolvable.push({ filteredIndex, dataUrl });
        } else {
            dispositions.push({
                photoIndex: filtered.originalIndexFor(filteredIndex),
                assignableIndex: filteredIndex,
                photoKey: scopePhotoDispositionKey(filtered.photos[filteredIndex], filteredIndex),
                outcome: 'omitted',
                scopeIndex: null,
                reason: 'unavailable-image',
            });
        }
    }

    // Route through callOpenRouter instead of raw fetch (AI-1 audit 2026-04-21).
    // callOpenRouter handles: 5-slot concurrency semaphore, 429 rate-limit retry
    // with exponential backoff, multi-key rotation + cooldown, 120s AbortSignal
    // timeout guard, and PipelineMonitor wrap. The pre-audit implementation was
    // bypassing ALL of those guards with a raw fetch(): a single hung OpenRouter
    // connection would stall the whole import pipeline.
    //
    // callOpenRouter accepts `messages` with mixed text+image_url content blocks
    // as-is (standard OpenRouter chat format), so the hybrid shape this service
    // needs works without requiring a new helper.
    const primaryModel = pickModelForTask(PHOTO_AUTO_ASSIGN_TASK, model);
    // No defaultModel: the registry owns this lane. A hardcoded id here is a
    // landmine — it stays dormant only while the task keeps `strongRecommendation`
    // in a DIFFERENT table, and nothing checks the two agree.
    const fallbackModel = pickModelForTask(PHOTO_AUTO_ASSIGN_FALLBACK_TASK);
    const callRoute = async ({ requestModel, requestTask, fallbackApplied, batch }) => {
        const batchDescriptions = batch.map((entry, batchIndex) => ({
            batchIndex,
            ...photoDescriptions[entry.filteredIndex],
        }));
        const prompt = `You are analysing one bounded batch of construction site photos for a building remediation quote. Review EVERY photo in this batch and return exactly one disposition for each batch-local photoIndex.

SCOPE ITEMS:
${scopeSummary.map(s => `[${s.index}] ${s.code}. ${s.title}: ${s.description}`).join('\n')}

PHOTO SELECTION RULES:
1. Each photo outcome is either "assigned" to exactly one scope item, or "omitted" with one allowed reason: weak-quality, blurred, redundant, off-topic, or no-matching-scope.
2. A location/unit tag after "— at:" is authoritative. Never place a Unit 7 photo on Unit 11 scope.
3. Quality over quantity: target the strongest 3 photos per scope item and never more than 6. Omit repetitive or weak images instead of padding the quote.
4. Return one unique disposition for every photoIndex 0 through ${batch.length - 1}; do not skip any photo and do not repeat an index.
5. For assigned: scopeIndex is an integer and reason is "assigned". For omitted: scopeIndex is null and reason is one allowed omission reason.

Also select the best batch-local heroPhotoIndex, preferring a clean overview over close-ups or marked-up images; null is allowed.
${hasAnyLocator ? 'Mention locator matches in the brief reasoning.\n' : ''}
Batch photo labels are interleaved immediately before their image below.`;
        const messageContent = [{ type: 'text', text: prompt }];
        batchDescriptions.forEach((description, batchIndex) => {
            messageContent.push({ type: 'text', text: `[${batchIndex}] ${description.name}` });
            messageContent.push({ type: 'image_url', image_url: { url: batch[batchIndex].dataUrl } });
        });
        const responseContent = await callOpenRouter({
            apiKey,
            model: requestModel,
            messages: [{
                role: 'user',
                content: messageContent,
            }],
            temperature: 0.3,
            maxTokens: getMaxOutputTokens(requestModel),
            pipelineId: PHOTO_AUTO_ASSIGN_TASK,
            stageName: fallbackApplied ? 'Stage 4 - Photo Auto-Assign fallback' : 'Stage 4 - Photo Auto-Assign primary',
            signal,
            responseFormat: PHOTO_AUTO_ASSIGN_RESPONSE_FORMAT,
            task: requestTask,
            timeoutMs: PHOTO_AUTO_ASSIGN_TIMEOUT_MS,
            projectId,
            modelRouteAttempt: {
                attemptedModel: requestModel,
                attemptNumber: fallbackApplied ? 2 : 1,
                source: fallbackApplied ? 'photo-auto-assign-fallback' : 'photo-auto-assign-primary',
                fallbackApplied,
                fallbackFromModel: fallbackApplied ? primaryModel : null,
                fallbackToModel: fallbackApplied ? requestModel : null,
                fallbackAttempt: fallbackApplied ? 1 : null,
                fallbackSource: fallbackApplied ? 'invalid-or-failed-primary-output' : null,
            },
        });
        return parseAndValidateAssignmentResponse(responseContent, {
            batchPhotoCount: batch.length,
            scopeCount: scope.length,
        });
    };

    let anyFallbackApplied = false;
    let firstPrimaryError = null;
    const reasoningParts = [];
    const heroCandidates = [];
    try {
        for (let start = 0; start < resolvable.length; start += batchSize) {
            const batch = resolvable.slice(start, start + batchSize);
            let parsedOutput;
            try {
                parsedOutput = await callRoute({
                    requestModel: primaryModel,
                    requestTask: PHOTO_AUTO_ASSIGN_TASK,
                    fallbackApplied: false,
                    batch,
                });
            } catch (err) {
                if (isAbortLike(err)) throw err;
                if (!firstPrimaryError) firstPrimaryError = err;
                if (!fallbackModel || fallbackModel === primaryModel) throw err;
                devLog(`[photo-auto-assign] Primary route failed (${primaryModel}); retrying visible fallback (${fallbackModel}): ${err?.message || err}`);
                parsedOutput = await callRoute({
                    requestModel: fallbackModel,
                    requestTask: PHOTO_AUTO_ASSIGN_FALLBACK_TASK,
                    fallbackApplied: true,
                    batch,
                });
                anyFallbackApplied = true;
            }

            for (const disposition of parsedOutput.dispositions) {
                const batchIndex = toIntegerIndex(disposition.photoIndex);
                const batchEntry = batch[batchIndex];
                const filteredIndex = batchEntry.filteredIndex;
                const originalIndex = filtered.originalIndexFor(filteredIndex);
                dispositions.push({
                    photoIndex: originalIndex,
                    assignableIndex: filteredIndex,
                    photoKey: scopePhotoDispositionKey(filtered.photos[filteredIndex], filteredIndex),
                    outcome: disposition.outcome,
                    scopeIndex: disposition.outcome === 'assigned' ? disposition.scopeIndex : null,
                    reason: disposition.reason,
                });
            }
            const batchHero = toIntegerIndex(parsedOutput.heroPhotoIndex);
            if (batchHero != null) {
                heroCandidates.push(filtered.originalIndexFor(batch[batchHero].filteredIndex));
            }
            const batchReasoning = typeof parsedOutput.reasoning === 'string' ? parsedOutput.reasoning.trim() : '';
            if (batchReasoning) reasoningParts.push(batchReasoning);
        }

        dispositions.sort((a, b) => a.assignableIndex - b.assignableIndex);
        if (dispositions.length !== filtered.photos.length) {
            throw photoAutoAssignInvalidOutput('Photo assignment did not produce exact disposition coverage.');
        }
        const assignments = dispositions
            .filter(disposition => disposition.outcome === 'assigned')
            .map(disposition => ({
                photoIndex: disposition.photoIndex,
                scopeIndex: disposition.scopeIndex,
            }));
        const aiPreferredOriginalHero = heroCandidates.find(index => Number.isInteger(index) && index >= 0) ?? null;
        const heroPhotoIndex = chooseHeroPhotoIndex(
            photos,
            aiPreferredOriginalHero,
            { scopeContext: scope },
        );
        const reasoning = reasoningParts.join(' ');
        const assignedPhotos = dispositions.filter(disposition => disposition.outcome === 'assigned').length;
        const omittedPhotos = dispositions.length - assignedPhotos;

        return {
            assignments,
            dispositions,
            heroPhotoIndex,
            reasoning,
            droppedDocPhotos: filtered.droppedCount,
            assignmentCoverage: {
                status: 'validated',
                assignablePhotos: filtered.photos.length,
                dispositionedPhotos: dispositions.length,
                assignedPhotos,
                omittedPhotos,
                complete: dispositions.length === filtered.photos.length,
                requiresReview: assignments.length === 0,
            },
            route: {
                task: anyFallbackApplied ? PHOTO_AUTO_ASSIGN_FALLBACK_TASK : PHOTO_AUTO_ASSIGN_TASK,
                model: anyFallbackApplied ? fallbackModel : primaryModel,
                fallbackApplied: anyFallbackApplied,
                primaryError: anyFallbackApplied ? (firstPrimaryError?.code || firstPrimaryError?.message || 'primary failed') : null,
            },
        };
    } catch (err) {
        if (isAbortLike(err)) {
            return null;
        }
        const finalError = new Error('Photo assignment failed on both the primary and fallback Lanes. No photo assignments were applied. Retry from Stage 4 or assign photos manually before final review.');
        finalError.name = 'PhotoAutoAssignError';
        finalError.code = 'photo_auto_assign_failed';
        finalError.retryable = true;
        finalError.primaryError = firstPrimaryError || err;
        finalError.fallbackError = firstPrimaryError ? err : null;
        throw finalError;
    }
}

/**
 * Apply an `{ assignments, heroPhotoIndex }` result to a scope array and
 * return the new scope. Pure - does not touch React state; the caller wraps
 * it in setScope. The returned scope preserves all existing images and only
 * APPENDS new ones (it never removes).
 *
 * @param {Array<Object>} scope     current scope array
 * @param {Array<Object>} photos    current photos array (for lookup by index)
 * @param {Array<{photoIndex:number, scopeIndex:number}>} assignments
 * @returns {{ nextScope:Array, assignedCount:number }}
 */
// v5.13.17: hard ceiling per scope item. Even if the AI returns 20 photos
// for one scope, we keep only the first 6 (the AI is told to rank them
// best-first). Three is the soft target, baked into the prompt; six is
// the absolute defensive max. Combined with the prompt rules these two
// gates keep scope items focused without collapsing the AI's judgement
// when a scope item legitimately needs many shots.
const MAX_IMAGES_PER_SCOPE_ITEM = 6;

export const scoreHeroPhoto = scoreQuoteHeroCandidate;

export function chooseHeroPhotoIndex(photos = [], preferredIndex = null, options = {}) {
    return chooseQuoteHeroPhotoIndex(photos, preferredIndex, options);
}

export function applyAutoAssignResult(scope, photos, assignments) {
    if (!Array.isArray(scope) || !Array.isArray(photos) || !Array.isArray(assignments)) {
        return { nextScope: scope, assignedCount: 0 };
    }
    const nextScope = scope.map(item => ({ ...item, images: item.images || [] }));

    // ── v5.34.6: dedup + unit-match precedence ──────────────────────────────
    // Peter 2026-05-19: "I don't want to see the same picture more than once
    // on a quote. Also, for properties or Quotes that span across multiple
    // Units, I want QuoteMaker to figure out which unit the photos are for,
    // to ensure they are assigned to the right unit."
    //
    // Pass A: photos with a unit-token that matches a scope item's unit-token
    // go to that scope item ONLY. The AI prompt is told this is a hard rule
    // (rule 2) but the post-processor enforces it independently in case the
    // AI returns a Unit 7 photo on a Unit 11 scope item — strata management
    // splits quotes by unit so a photo on the wrong unit's invoice is a real
    // billing-correctness bug, not just a cosmetic mismatch.
    //
    // Pass B: photos NOT matched by Pass A are walked in assignment order;
    // the first scope item that wins a photo locks it (the AI is told to
    // rank best-first per scope item, so the first scope item that names a
    // photo as its top pick wins it). Subsequent assignments of the same
    // photo to other scope items are dropped.
    //
    // Both passes respect the MAX_IMAGES_PER_SCOPE_ITEM cap.

    // Pre-compute unit tokens for each scope item.
    const scopeUnitTokens = nextScope.map(item => {
        const text = `${item.title || ''} ${item.description || ''} ${item.clientDescription || ''}`;
        return extractUnitTokens(text);
    });

    // Pre-compute unit tokens for each photo (from aiLabel, name, originalName,
    // context, description, and any locator strings on the photo).
    const photoUnitTokens = photos.map(photo => {
        if (!photo) return new Set();
        const text = [
            photo.aiLabel,
            photo.name,
            photo.originalName,
            photo.description,
            photo.context,
            photo.aiAtLocator,
            photo.locator,
        ].filter(Boolean).join(' ');
        return extractUnitTokens(text);
    });

    // Did the project as a whole carry multiple unit identifiers? If yes,
    // we KNOW this is a strata-split quote and the unit-match rule is hard.
    // If no, the unit-match path effectively no-ops and Pass B handles
    // everything as a normal dedup'd single-unit quote.
    const allScopeUnits = new Set();
    for (const set of scopeUnitTokens) for (const t of set) allScopeUnits.add(t);
    const isMultiUnitQuote = allScopeUnits.size >= 2;

    const photoAssigned = new Set(); // photo IDs that have been placed
    // Seed from existing assignments so re-fires (new photo added, manual
    // retry) cannot re-place an already-placed photo onto a second scope
    // item. Without this seed, the post-processor dedup only spans the
    // current call — the AI re-receives the full photo list each run, may
    // route an already-placed photo elsewhere, and Pass B accepts it because
    // the Set was just initialised empty. Symptom: small photo set spread
    // thin across many scope items. Why this is load-bearing: the prompt's
    // Rule 1 ("each photo on at most one scope item") relies on the AI
    // seeing prior state, which it doesn't — the post-processor is the only
    // backstop and it needs the seed to do that job across calls.
    for (const item of nextScope) {
        if (!item || !Array.isArray(item.images)) continue;
        for (const img of item.images) {
            const id = img?.id;
            if (id) photoAssigned.add(id);
        }
    }
    let assignedCount = 0;
    let droppedCount = 0;
    let unitMatchedCount = 0;
    let dedupedCount = 0;

    // Pre-compute label/title tokens for overlap scoring (Pass A
    // disambiguation when one unit has multiple scope items, e.g. Unit 7
    // Balcony + Unit 7 Bathroom — the "balcony" / "bathroom" word in the
    // photo's label tells us which item to pick).
    const scopeLabelTokens = nextScope.map(item =>
        tokenisePhotoLabel(`${item.title || ''} ${item.description || ''} ${item.clientDescription || ''}`)
    );
    const photoLabelTokens = photos.map(photo => {
        if (!photo) return new Set();
        return tokenisePhotoLabel([
            photo.aiLabel, photo.name, photo.originalName,
            photo.description, photo.context, photo.aiAtLocator, photo.locator,
        ].filter(Boolean).join(' '));
    });

    // Pass A — unit-match precedence. For each photo with unit tokens, find
    // all scope items whose unit tokens intersect. If exactly one matches,
    // claim it. If multiple match (e.g. same unit, two rooms), pick the
    // scope item with the highest text-overlap with the photo's label
    // (Unit 7 east BALCONY → Unit 7 Balcony, not Unit 7 Bathroom).
    for (let pIdx = 0; pIdx < photos.length; pIdx++) {
        const pTokens = photoUnitTokens[pIdx];
        if (!pTokens || pTokens.size === 0) continue;
        // Collect ALL scope items whose unit-token set intersects.
        const candidates = [];
        for (let sIdx = 0; sIdx < nextScope.length; sIdx++) {
            if (unitTokensIntersect(pTokens, scopeUnitTokens[sIdx])) {
                candidates.push(sIdx);
            }
        }
        if (candidates.length === 0) continue;

        // Pick the best candidate. If 1, that's it. If >1, score by label
        // overlap (photo's label tokens ∩ scope item's title+desc tokens),
        // then by original order to break ties.
        let claimedScopeIdx;
        if (candidates.length === 1) {
            claimedScopeIdx = candidates[0];
        } else {
            const pLabel = photoLabelTokens[pIdx];
            let bestIdx = candidates[0];
            let bestScore = tokenOverlapScore(pLabel, scopeLabelTokens[candidates[0]]);
            for (let i = 1; i < candidates.length; i++) {
                const score = tokenOverlapScore(pLabel, scopeLabelTokens[candidates[i]]);
                if (score > bestScore) {
                    bestScore = score;
                    bestIdx = candidates[i];
                }
            }
            claimedScopeIdx = bestIdx;
        }

        const photo = getClientFacingPhoto(photos[pIdx]);
        if (!photo || photoAssigned.has(photo.id)) continue;
        const target = nextScope[claimedScopeIdx];
        if (target.images.find(img => img.id === photo.id)) {
            photoAssigned.add(photo.id);
            continue;
        }
        if (target.images.length >= MAX_IMAGES_PER_SCOPE_ITEM) {
            droppedCount++;
            continue;
        }
        target.images = [...target.images, photo];
        photoAssigned.add(photo.id);
        assignedCount++;
        unitMatchedCount++;
    }

    // Pass B — walk the AI assignments in order, skipping any photo already
    // claimed by Pass A or already claimed earlier in Pass B (this is the
    // cross-scope dedup that Peter asked for: no photo appears twice).
    for (const assignment of assignments) {
        const photoIndex = toIntegerIndex(assignment?.photoIndex);
        const scopeIndex = toIntegerIndex(assignment?.scopeIndex);
        if (photoIndex == null || scopeIndex == null) continue;
        if (photoIndex < 0 || photoIndex >= photos.length) continue;
        if (scopeIndex < 0 || scopeIndex >= nextScope.length) continue;
        const photo = getClientFacingPhoto(photos[photoIndex]);
        if (!photo) continue;
        // Cross-scope dedup: if this photo has already been assigned by
        // either pass, skip it. This is the load-bearing fix for "I don't
        // want to see the same picture more than once on a quote."
        if (photoAssigned.has(photo.id)) {
            dedupedCount++;
            continue;
        }
        // In a multi-unit quote, refuse to assign a unit-tokened photo to
        // a scope item whose unit doesn't match. Pass A would have handled
        // a matched assignment; arriving here means the AI either ignored
        // its locator OR no scope item matched, and we'd rather drop it
        // than misroute it onto the wrong unit's invoice.
        if (isMultiUnitQuote) {
            const pTokens = photoUnitTokens[photoIndex];
            const sTokens = scopeUnitTokens[scopeIndex];
            if (pTokens && pTokens.size > 0 && sTokens && sTokens.size > 0 && !unitTokensIntersect(pTokens, sTokens)) {
                // Photo carries a unit, scope item carries a (different) unit
                // — never put a Unit 7 photo on a Unit 11 invoice.
                droppedCount++;
                continue;
            }
        }
        const target = nextScope[scopeIndex];
        if (target.images.find(img => img.id === photo.id)) {
            photoAssigned.add(photo.id);
            continue;
        }
        if (target.images.length >= MAX_IMAGES_PER_SCOPE_ITEM) {
            droppedCount++;
            continue;
        }
        target.images = [...target.images, photo];
        photoAssigned.add(photo.id);
        assignedCount++;
    }

    if (unitMatchedCount > 0 || dedupedCount > 0 || droppedCount > 0) {
        devLog(`[photo-auto-assign] applyAutoAssignResult: unit-matched=${unitMatchedCount}, deduped-across-items=${dedupedCount}, capped/cross-unit-dropped=${droppedCount}, isMultiUnit=${isMultiUnitQuote}`);
    }
    return { nextScope, assignedCount, unitMatchedCount, dedupedCount, droppedCount, isMultiUnitQuote };
}


/**
 * Resolve a photo object to a data URL string.
 * Non-React equivalent of usePhotoSrc - for services that need an async data URL.
 *
 * Priority:
 *   1. inline data URL
 *   2. project-owned Blob-store record
 *   3. available S3 key
 *   4. independent file_metadata preview receipt
 *   5. current-session display URLs
 *   6. null - resolution failed
 *
 * @param {Object} photo - Photo object with .data, .blobId, or .s3Key
 * @param {string|null} expectedProjectId - When set, refuse a blob owned by another project
 * @returns {Promise<string|null>} data URL string or null
 */
function blobToDataUrl(blob) {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
    });
}

async function sameOriginFetchUrl(url) {
    if (typeof url !== 'string' || !url.trim()) return null;
    const candidate = url.trim();
    if (!isRawMinioUrl(candidate)) return candidate;
    try {
        const { toSameOriginDisplayUrl } = await import('./file-upload-service');
        const safe = await toSameOriginDisplayUrl(candidate);
        return safe && !isRawMinioUrl(safe) ? safe : null;
    } catch {
        return null;
    }
}

async function fetchPhotoUrlAsDataUrl(url) {
    const fetchUrl = await sameOriginFetchUrl(url);
    if (!fetchUrl) return { dataUrl: null, status: null };
    if (/^data:image\//i.test(fetchUrl)) return { dataUrl: fetchUrl, status: 200 };
    try {
        const response = await fetch(fetchUrl);
        if (!response.ok) return { dataUrl: null, status: response.status };
        const blob = await response.blob();
        return {
            dataUrl: blob instanceof Blob ? await blobToDataUrl(blob) : null,
            status: response.status,
        };
    } catch {
        return { dataUrl: null, status: null };
    }
}

/**
 * Resolve one photo without allowing a browser-local blob owned by another
 * project to outrank its durable cloud/file receipts.
 *
 * @param {*} photo
 * @param {string|null} [expectedProjectId]
 * @returns {Promise<string|null>}
 */
export async function resolvePhotoDataUrl(photo, expectedProjectId = null) {
    if (!photo) return null;

    // Priority 1: inline data URL
    if (typeof photo.data === 'string' && photo.data.startsWith('data:')) {
        return photo.data;
    }
    if (typeof photo.dataUrl === 'string' && photo.dataUrl.startsWith('data:')) {
        return photo.dataUrl;
    }

    // Priority 2: blob store
    if (photo.blobId) {
        try {
            const { getPhotoBlob } = await import('./photo-blob-store');
            const blob = await getPhotoBlob(photo.blobId, expectedProjectId);
            if (blob instanceof Blob) {
                return blobToDataUrl(blob);
            }
        } catch (err) {
            console.warn('[photo-auto-assign] resolvePhotoDataUrl blob read failed:', err?.message);
        }
    }

    // Priority 3: S3 signed URL. A negative-cache receipt belongs to this
    // alias only; independent metadata/session sources still get a chance.
    if (photo.s3Key && !fileReferenceUnavailableReason(photo.s3Key)) {
        try {
            const { getFileUrl, markFileKeyMissing } = await import('./file-upload-service');
            const signedUrl = await getFileUrl(photo.s3Key);
            if (signedUrl) {
                const { dataUrl, status } = await fetchPhotoUrlAsDataUrl(signedUrl);
                if (dataUrl) return dataUrl;
                // Gone-class: metadata row exists but the object is gone from
                // storage (the proxy answers 410/404). Feed the negative cache
                // so the next render doesn't re-mint a doomed URL.
                if (status === 404 || status === 410) {
                    markFileKeyMissing(photo.s3Key);
                }
            }
        } catch (err) {
            // '(404, cached)' = negative-cached dead key, already reported once.
            if (!String(err?.message || '').includes('cached)')) {
                console.warn('[photo-auto-assign] resolvePhotoDataUrl S3 read failed:', err?.message);
            }
        }
    }

    // Priority 4: file_metadata is a durable lookup seam independent of s3Key.
    // A stale/cached-missing S3 alias must not blank a photo that still has a
    // tenant-verified metadata preview row.
    if (photo.fileMetadataId && !fileReferenceUnavailableReason(photo.fileMetadataId)) {
        try {
            const { DataServiceAPI } = await import('./data-service-api');
            const meta = await DataServiceAPI.files.getPreviewMeta(photo.fileMetadataId);
            if (meta?.url && meta.previewType !== 'unsupported') {
                const { dataUrl } = await fetchPhotoUrlAsDataUrl(meta.url);
                if (dataUrl) return dataUrl;
            }
        } catch (err) {
            console.warn('[photo-auto-assign] resolvePhotoDataUrl file metadata read failed:', err?.message);
        }
    }

    // Priority 5: current-session display URLs and persisted thumbnails.
    // Keep these after durable refs so stale blob/session URLs from project
    // history cannot mask portable cloud backing. Raw MinIO URLs are converted
    // to the same-origin proxy first and are never handed directly to fetch().
    const directUrls = [
        photo.thumbnailUrl,
        photo.previewUrl,
        photo.signedUrl,
        photo.url,
        typeof photo.data === 'string' && photo.data.startsWith('blob:') ? photo.data : null,
        typeof photo.dataUrl === 'string' && photo.dataUrl.startsWith('blob:') ? photo.dataUrl : null,
    ].filter((value, index, values) => (
        typeof value === 'string' && value && values.indexOf(value) === index
    ));
    for (const directUrl of directUrls) {
        const { dataUrl } = await fetchPhotoUrlAsDataUrl(directUrl);
        if (dataUrl) return dataUrl;
        if (!isRawMinioUrl(directUrl)) {
            console.warn('[photo-auto-assign] resolvePhotoDataUrl direct URL read failed');
        }
    }

    return null;
}
